import { useLayoutEffect, useRef } from "react";
import { useNativeWebMCP } from "./nativeWebMCP";
import type { ToolContext } from "./types";
import { CanvasTools } from "./canvasTools";
import { drawingPatchSchema, drawingReadSchema } from "./canvasSchemas";
import { toolDescriptions } from "./toolDocumentation";

export interface UseExcalidrawWebMCPOptions {
  getToken: () => Promise<string>;
  excalidrawAPI: import("@excalidraw/excalidraw/types").ExcalidrawImperativeAPI | null;
  driveId: string;
  itemId: string;
  drawingId: string;
  isReadOnly: boolean;
  legacyStorageDisabled?: boolean;
  /** Navigation/local-draft generation when an API instance is reused. */
  canvasIdentity?: string;
}

/** Exactly two canvas tools; application operations have a separate lifetime. */
export function useExcalidrawWebMCP(options: UseExcalidrawWebMCPOptions): void {
  const context = useRef<ToolContext>({ ...options, legacyStorageDisabled: true });
  const tools = useRef<CanvasTools | null>(null);
  useLayoutEffect(() => {
    context.current = { ...options, legacyStorageDisabled: true };
  });
  useLayoutEffect(() => {
    const instance = new CanvasTools(() => context.current);
    tools.current = instance;
    return () => {
      instance.dispose();
      if (tools.current === instance) tools.current = null;
    };
  }, [options.excalidrawAPI, options.drawingId, options.canvasIdentity]);
  const unavailable = () =>
    JSON.stringify({
      ok: false,
      error: {
        code: "NOT_READY",
        message: "No canvas is ready.",
        recovery: "Open a canvas and retry.",
      },
    });
  useNativeWebMCP({
    name: "drawing_read",
    enabled: !!options.excalidrawAPI,
    readOnly: true,
    description: toolDescriptions.drawing_read,
    inputSchema: drawingReadSchema,
    handler: async (input: object, signal) => tools.current?.read(input, signal) ?? unavailable(),
  });
  useNativeWebMCP({
    name: "drawing_patch",
    enabled: !!options.excalidrawAPI,
    description: toolDescriptions.drawing_patch,
    inputSchema: drawingPatchSchema,
    handler: async (input: object, signal) => tools.current?.patch(input, signal) ?? unavailable(),
  });
}
